import { createTheme } from '@mui/material/styles';

// Triton & Partners Design System - Elegantes, klassisches und konservatives Design
const TritonTheme = createTheme({
  palette: {
    primary: {
      main: '#1a365d', // Dunkles Marine-Blau (Triton Hauptfarbe)
      light: '#2d3748',
      dark: '#0f2027',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4a5568', // Elegantes Grau
      light: '#718096',
      dark: '#2d3748',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#d69e2e', // Dezentes Gold für Akzente
      light: '#ed8936',
      dark: '#b7791f',
    },
    background: {
      default: '#f7fafc', // Sehr helles Grau
      paper: '#ffffff',
      contrast: '#1a365d',
    },
    text: {
      primary: '#2d3748', // Dunkles Grau für beste Lesbarkeit
      secondary: '#4a5568',
      disabled: '#a0aec0',
    },
    divider: '#e2e8f0',
    success: {
      main: '#38a169',
      light: '#68d391',
      dark: '#25855a',
    },
    warning: {
      main: '#d69e2e',
      light: '#fbb042',
      dark: '#b7791f',
    },
    error: {
      main: '#e53e3e',
      light: '#fc8181',
      dark: '#c53030',
    },
    info: {
      main: '#3182ce',
      light: '#63b3ed',
      dark: '#2c5282',
    },
  },
  typography: {
    fontFamily: [
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 300,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#1a365d',
      marginBottom: '1rem',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 400,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#1a365d',
      marginBottom: '0.875rem',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#2d3748',
      marginBottom: '0.75rem',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#2d3748',
      marginBottom: '0.75rem',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#2d3748',
      marginBottom: '0.5rem',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#4a5568',
      marginBottom: '0.5rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#4a5568',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#4a5568',
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#2d3748',
    },
    subtitle2: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#4a5568',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: '#718096',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#718096',
    },
  },
  spacing: 8, // 8px als Basis-Einheit
  shape: {
    borderRadius: 8, // Subtile Rundungen
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(26, 54, 93, 0.08), 0px 1px 2px rgba(26, 54, 93, 0.06)',
    '0px 1px 5px rgba(26, 54, 93, 0.08), 0px 2px 4px rgba(26, 54, 93, 0.06)',
    '0px 1px 8px rgba(26, 54, 93, 0.1), 0px 3px 6px rgba(26, 54, 93, 0.08)',
    '0px 2px 12px rgba(26, 54, 93, 0.12), 0px 4px 8px rgba(26, 54, 93, 0.08)',
    '0px 3px 16px rgba(26, 54, 93, 0.12), 0px 6px 12px rgba(26, 54, 93, 0.08)',
    '0px 4px 20px rgba(26, 54, 93, 0.15), 0px 8px 16px rgba(26, 54, 93, 0.1)',
    '0px 6px 24px rgba(26, 54, 93, 0.15), 0px 12px 20px rgba(26, 54, 93, 0.1)',
    '0px 8px 28px rgba(26, 54, 93, 0.15), 0px 16px 24px rgba(26, 54, 93, 0.1)',
    '0px 9px 32px rgba(26, 54, 93, 0.16), 0px 18px 28px rgba(26, 54, 93, 0.12)',
    '0px 10px 36px rgba(26, 54, 93, 0.16), 0px 20px 32px rgba(26, 54, 93, 0.12)',
    '0px 11px 40px rgba(26, 54, 93, 0.17), 0px 22px 36px rgba(26, 54, 93, 0.13)',
    '0px 12px 44px rgba(26, 54, 93, 0.17), 0px 24px 40px rgba(26, 54, 93, 0.13)',
    '0px 13px 48px rgba(26, 54, 93, 0.18), 0px 26px 44px rgba(26, 54, 93, 0.14)',
    '0px 14px 52px rgba(26, 54, 93, 0.18), 0px 28px 48px rgba(26, 54, 93, 0.14)',
    '0px 15px 56px rgba(26, 54, 93, 0.19), 0px 30px 52px rgba(26, 54, 93, 0.15)',
    '0px 16px 60px rgba(26, 54, 93, 0.19), 0px 32px 56px rgba(26, 54, 93, 0.15)',
    '0px 17px 64px rgba(26, 54, 93, 0.2), 0px 34px 60px rgba(26, 54, 93, 0.16)',
    '0px 18px 68px rgba(26, 54, 93, 0.2), 0px 36px 64px rgba(26, 54, 93, 0.16)',
    '0px 19px 72px rgba(26, 54, 93, 0.21), 0px 38px 68px rgba(26, 54, 93, 0.17)',
    '0px 20px 76px rgba(26, 54, 93, 0.21), 0px 40px 72px rgba(26, 54, 93, 0.17)',
    '0px 21px 80px rgba(26, 54, 93, 0.22), 0px 42px 76px rgba(26, 54, 93, 0.18)',
    '0px 22px 84px rgba(26, 54, 93, 0.22), 0px 44px 80px rgba(26, 54, 93, 0.18)',
    '0px 23px 88px rgba(26, 54, 93, 0.23), 0px 46px 84px rgba(26, 54, 93, 0.19)',
    '0px 25px 100px rgba(26, 54, 93, 0.25), 0px 50px 90px rgba(26, 54, 93, 0.2)',
  ],
  components: {
    // Button-Styling im Triton-Stil
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '10px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(26, 54, 93, 0.15)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #0f2027 0%, #1a365d 100%)',
            boxShadow: '0px 4px 12px rgba(26, 54, 93, 0.25)',
          },
          '&:disabled': {
            background: '#a0aec0',
            color: '#ffffff',
          },
        },
        outlined: {
          borderColor: '#1a365d',
          color: '#1a365d',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#0f2027',
            backgroundColor: 'rgba(26, 54, 93, 0.04)',
            borderWidth: '2px',
          },
        },
        text: {
          color: '#1a365d',
          '&:hover': {
            backgroundColor: 'rgba(26, 54, 93, 0.04)',
          },
        },
      },
    },
    
    // AppBar-Styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
          boxShadow: '0px 4px 20px rgba(26, 54, 93, 0.15)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    
    // Card-Styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0px 1px 3px rgba(26, 54, 93, 0.08), 0px 1px 2px rgba(26, 54, 93, 0.06)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(26, 54, 93, 0.12), 0px 8px 16px rgba(26, 54, 93, 0.08)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    
    // Paper-Styling
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(26, 54, 93, 0.08), 0px 1px 2px rgba(26, 54, 93, 0.06)',
        },
      },
    },
    
    // Drawer-Styling
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #ffffff 0%, #f7fafc 100%)',
          borderRight: '1px solid #e2e8f0',
          boxShadow: '4px 0px 12px rgba(26, 54, 93, 0.08)',
        },
      },
    },
    
    // List-Item-Styling
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(26, 54, 93, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(26, 54, 93, 0.08)',
            borderLeft: '4px solid #1a365d',
            '&:hover': {
              backgroundColor: 'rgba(26, 54, 93, 0.12)',
            },
          },
        },
      },
    },
    
    // TextField-Styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#e2e8f0',
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1a365d',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#4a5568',
            '&.Mui-focused': {
              color: '#1a365d',
            },
          },
        },
      },
    },
    
    // Table-Styling
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f7fafc',
          '& .MuiTableCell-head': {
            color: '#2d3748',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '2px solid #e2e8f0',
          },
        },
      },
    },
    
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: '#fafbfc',
          },
          '&:hover': {
            backgroundColor: 'rgba(26, 54, 93, 0.04)',
          },
        },
      },
    },
    
    // Chip-Styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        filled: {
          backgroundColor: 'rgba(26, 54, 93, 0.1)',
          color: '#1a365d',
          '&:hover': {
            backgroundColor: 'rgba(26, 54, 93, 0.15)',
          },
        },
      },
    },
    
    // Dialog-Styling
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          padding: '8px',
          boxShadow: '0px 8px 32px rgba(26, 54, 93, 0.15)',
        },
      },
    },
    
    // Tab-Styling
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#1a365d',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: '#4a5568',
          '&.Mui-selected': {
            color: '#1a365d',
          },
          '&:hover': {
            color: '#2d3748',
          },
        },
      },
    },
  },
});

// Zusätzliche benutzerdefinierte Stile
export const tritonStyles = {
  // Gradient-Hintergründe
  gradients: {
    primary: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
    secondary: 'linear-gradient(135deg, #4a5568 0%, #718096 100%)',
    accent: 'linear-gradient(135deg, #d69e2e 0%, #ed8936 100%)',
    light: 'linear-gradient(180deg, #ffffff 0%, #f7fafc 100%)',
  },
  
  // Schatten-Varianten
  elevations: {
    subtle: '0px 1px 3px rgba(26, 54, 93, 0.08), 0px 1px 2px rgba(26, 54, 93, 0.06)',
    medium: '0px 4px 12px rgba(26, 54, 93, 0.12), 0px 8px 16px rgba(26, 54, 93, 0.08)',
    high: '0px 8px 32px rgba(26, 54, 93, 0.15), 0px 16px 24px rgba(26, 54, 93, 0.1)',
  },
  
  // Animationen
  transitions: {
    standard: 'all 0.2s ease-in-out',
    slow: 'all 0.3s ease-in-out',
    fast: 'all 0.15s ease-in-out',
  },
  
  // Abstandsystem
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
};

export default TritonTheme;
